import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Download, Loader2, RotateCcw } from 'lucide-react';
import { Modal, ModalFooter, Button, Select, DatePicker, ConfirmationModal } from '@/components/ui';
import { SuggestedItem } from '../types';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/error';
import { useLinkedInTokenStatus } from '@/hooks/useLinkedInTokenStatus';
import api from '@/utils/api';

// Custom hooks
import { usePostData } from '../hooks/usePostData';
import { useTemplates } from '../hooks/useTemplates';
import { useImageUpload } from '../hooks/useImageUpload';
import { usePostRender } from '../hooks/usePostRender';

// Components
import {
  BasicInfoSection,
  MediaSection,
  BrandingSection,
  AdditionalFieldsSection,
} from './PostFormSections';

// Utils
import { datesAreEqual, buildRenderPayload, buildPostText, buildSavePayload, formatTemplateName } from '../utils/postHelpers';

interface EditContentModalProps {
  item: SuggestedItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (item: SuggestedItem, updates: any) => Promise<void>;
  onRefetchCalendar?: () => Promise<void>;
}

interface VersionPayload {
  templateName: string;
  category: string;
  title: string;
  subtitle?: string;
  mainText: string;
  cta?: string;
  logo?: string;
  photo?: string;
  background?: string;
  image1?: string;
  image2?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  fontForText?: string;
  imageGeneratedTopic?: string;
  bullets?: string[];
  date?: string;
  time?: string;
  speakerPhoto?: string;
  speakerRole?: string;
  phone?: string;
  website?: string;
  backgroundColor?: string;
  brandHandle?: string;
  brandName?: string;
}

interface RenderPayload {
  templateName: string;
  title?: string;
  subtitle?: string;
  mainText?: string;
  cta?: string;
  logo?: string;
  photo?: string;
  background?: string;
  image1?: string;
  image2?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  fontForText?: string;
  imageGeneratedTopic?: string;
  bullets?: string[];
  date?: string;
  time?: string;
  speakerPhoto?: string;
  speakerRole?: string;
  phone?: string;
  website?: string;
  backgroundColor?: string;
  brandHandle?: string;
  brandName?: string;
  versionId?: number;
  postId?: string;
}

interface PublishPayload {
  text: string;
  postId: number;
  title?: string;
  imageUrl?: string;
}

/**
 * Edit Content Modal - Refactored for better maintainability
 */
export const EditContentModal: FC<EditContentModalProps> = ({
  item,
  isOpen,
  onClose,
  onSave,
  onRefetchCalendar,
}) => {
  const { addToast } = useToast();
  const { hasToken: hasLinkedInToken } = useLinkedInTokenStatus();

  // State management via custom hooks
  const { postData, updatePostData, originalPlannedDateRef, savedTemplateNameRef } = usePostData(item, isOpen);

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    loadingTemplates,
    previousTemplateRef,
    fetchTemplateMeta,
    shouldShowField,
  } = useTemplates(postData.contentType, isOpen, item?.id?.toString(), savedTemplateNameRef);

  const { uploadImage, uploadingLogo, uploadingPhoto, uploadingBackground, uploadingImage1, uploadingImage2, uploadingSpeakerPhoto } = useImageUpload();

  const { imageUrl, setImageUrl, isLoadingRender, setIsLoadingRender, triggerRender, fetchUpdatedRender } = usePostRender(item?.id?.toString(), isOpen);

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [documentInfo, setDocumentInfo] = useState<{ filename: string; mxmUploadId: string } | null>(null);
  const [loadingDocumentUrl, setLoadingDocumentUrl] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Track if defaults have been applied to avoid re-applying them
  const defaultsAppliedRef = useRef(false);

  // Debounce timer for auto-saving template version
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoSavingRef = useRef(false);

  // Map to store signed URLs for S3 keys (for immediate display after upload)
  // Key: S3 key (e.g., "images/abc.png"), Value: signed URL
  const displayUrlMapRef = useRef<Map<string, string>>(new Map());

  const isPublished = postData.postStatus === 'published';

  // Helper to get full image URL for display
  // Uses signedUrl from displayUrlMap if available (for newly uploaded images)
  // Otherwise uses the path as-is (backend will provide signed URLs when fetching)
  const getImageUrl = (path: string): string => {
    if (!path) return '';
    // If already a full URL (http/https), return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    // Check if we have a signed URL for this S3 key in the display map
    const signedUrl = displayUrlMapRef.current.get(path);
    if (signedUrl) return signedUrl;
    // If it's a local path (starts with /), prepend baseURL
    if (path.startsWith('/')) {
      const baseURL = api.defaults.baseURL || '';
      return `${baseURL}${path}`;
    }
    // For S3 keys, return as-is - backend will convert to signed URL on fetch
    // This means newly uploaded images won't display until save/refresh
    // But we handle that above with displayUrlMap
    return path;
  };

  // Helper to build version payload
  const buildVersionPayload = (): VersionPayload => {
    // Use company logo as fallback if post logo is empty
    let logoToUse = postData.logo;
    const companyLogo = item?.company?.logoUrl;
    if (!logoToUse && companyLogo) {
      logoToUse = companyLogo;
    }

    return {
      templateName: selectedTemplate,
      category: postData.contentType || '',
      title: postData.title,
      subtitle: postData.subtitle || undefined,
      mainText: postData.mainText,
      cta: postData.cta || undefined,
      logo: logoToUse || undefined,
      photo: postData.photo || undefined,
      background: postData.background || undefined,
      image1: postData.image1 || undefined,
      image2: postData.image2 || undefined,
      color1: postData.color1 || undefined,
      color2: postData.color2 || undefined,
      color3: postData.color3 || undefined,
      fontForText: postData.fontForText || undefined,
      imageGeneratedTopic: postData.imageGeneratedTopic || undefined,
      bullets: postData.bullets && postData.bullets.length > 0 ? postData.bullets : undefined,
      date: postData.date && postData.date.trim() !== '' ? postData.date : undefined,
      time: postData.time || undefined,
      speakerPhoto: postData.speakerPhoto || undefined,
      speakerRole: postData.speakerRole || undefined,
      phone: postData.phone || undefined,
      website: postData.website || undefined,
      backgroundColor: postData.backgroundColor || undefined,
      brandHandle: postData.brandHandle || undefined,
      brandName: postData.brandName || undefined,
    };
  };

  // Auto-save template version when images/config change
  const autoSaveTemplateVersion = useCallback(async () => {
    if (!item?.id || !selectedTemplate || !postData.contentType || isAutoSavingRef.current) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Debounce: wait 1 second after last change before saving
    autoSaveTimerRef.current = setTimeout(async () => {
      if (!item?.id || !selectedTemplate || !postData.contentType) return;

      isAutoSavingRef.current = true;
      try {
        const versionPayload = buildVersionPayload();
        await api.post(`/mxm/posts/${item.id}/template-versions`, versionPayload);
        // Silently save - don't show toast to avoid spam
      } catch (error: unknown) {
        console.error('Failed to auto-save template version:', error);
        // Don't show error toast for auto-save failures
      } finally {
        isAutoSavingRef.current = false;
      }
    }, 1000);
  }, [item?.id, selectedTemplate, postData]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Image upload handlers - store S3 key in postData, signedUrl in display map
  const handleLogoUpload = async (file: File) => {
    const result = await uploadImage(file, 'logo');
    if (result) {
      // Store S3 key in postData (for saving to DB)
      updatePostData({ logo: result.s3Key });
      // Store signedUrl in display map (for immediate display)
      displayUrlMapRef.current.set(result.s3Key, result.signedUrl);
      // Images are only saved when user clicks Save or Refresh
    }
  };

  const handlePhotoUpload = async (file: File) => {
    const result = await uploadImage(file, 'photo');
    if (result) {
      updatePostData({ photo: result.s3Key });
      displayUrlMapRef.current.set(result.s3Key, result.signedUrl);
      // Images are only saved when user clicks Save or Refresh
    }
  };

  const handleBackgroundUpload = async (file: File) => {
    const result = await uploadImage(file, 'background');
    if (result) {
      updatePostData({ background: result.s3Key });
      displayUrlMapRef.current.set(result.s3Key, result.signedUrl);
      // Images are only saved when user clicks Save or Refresh
    }
  };

  const handleImage1Upload = async (file: File) => {
    const result = await uploadImage(file, 'image1');
    if (result) {
      updatePostData({ image1: result.s3Key });
      displayUrlMapRef.current.set(result.s3Key, result.signedUrl);
      // Images are only saved when user clicks Save or Refresh
    }
  };

  const handleImage2Upload = async (file: File) => {
    const result = await uploadImage(file, 'image2');
    if (result) {
      updatePostData({ image2: result.s3Key });
      displayUrlMapRef.current.set(result.s3Key, result.signedUrl);
      // Images are only saved when user clicks Save or Refresh
    }
  };

  const handleSpeakerPhotoUpload = async (file: File) => {
    const result = await uploadImage(file, 'speakerPhoto');
    if (result) {
      updatePostData({ speakerPhoto: result.s3Key });
      displayUrlMapRef.current.set(result.s3Key, result.signedUrl);
      // Images are only saved when user clicks Save or Refresh
    }
  };

  // Reset defaults applied flag and clear display URL map when modal closes or item changes
  useEffect(() => {
    if (!isOpen) {
      defaultsAppliedRef.current = false;
      displayUrlMapRef.current.clear(); // Clear signed URLs when modal closes
      setDocumentInfo(null); // Clear document info when modal closes
    }
  }, [isOpen, item?.id]);

  // Fetch document info when modal opens
  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (!postData.document || !isOpen) {
        setDocumentInfo(null);
        return;
      }

      // Check if document is a URL (legacy data) or MXM upload ID (UUID)
      // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
      const isUrl = postData.document.startsWith('http://') || postData.document.startsWith('https://');
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(postData.document);

      // Only fetch if it's a UUID (MXM upload ID), not a URL
      if (isUrl || !isUuid) {
        setDocumentInfo(null);
        return;
      }

      // postData.document contains MXMUpload ID (UUID)
      try {
        const response = await api.get(`/upload/${postData.document}/info`);
        if (response.data?.upload?.originalFilename) {
          setDocumentInfo({
            filename: response.data.upload.originalFilename,
            mxmUploadId: postData.document,
          });
        } else {
          setDocumentInfo(null);
        }
      } catch (error: unknown) {
        console.error('Failed to fetch document info:', error);
        setDocumentInfo(null);
      }
    };

    fetchDocumentInfo();
  }, [postData.document, isOpen]);

  // Apply default branding when template is first loaded
  useEffect(() => {
    const applyDefaultBranding = async () => {
      if (!selectedTemplate || !isOpen || !item?.id || defaultsAppliedRef.current) return;
      if (!postData.contentType) return; // Wait for postData to be loaded

      // Mark as applied immediately to prevent re-runs
      defaultsAppliedRef.current = true;

      const metaData = await fetchTemplateMeta(selectedTemplate, item?.id);

      // Apply default branding if available and fields are empty
      if (metaData?.defaultBranding) {
        const updates: any = {};
        if (metaData.defaultBranding.color1 && !postData.color1) updates.color1 = metaData.defaultBranding.color1;
        if (metaData.defaultBranding.color2 && !postData.color2) updates.color2 = metaData.defaultBranding.color2;
        if (metaData.defaultBranding.color3 && !postData.color3) updates.color3 = metaData.defaultBranding.color3;
        if (metaData.defaultBranding.logo && !postData.logo) updates.logo = metaData.defaultBranding.logo;
        if (metaData.defaultBranding['font-for-text'] && !postData.fontForText) updates.fontForText = metaData.defaultBranding['font-for-text'];
        if (metaData.defaultBranding.image1 && !postData.image1) updates.image1 = metaData.defaultBranding.image1;
        if (metaData.defaultBranding.image2 && !postData.image2) updates.image2 = metaData.defaultBranding.image2;

        if (Object.keys(updates).length > 0) {
          updatePostData(updates);
        }
      }
    };

    // Only apply defaults once when the template is selected and postData is loaded
    if (selectedTemplate && postData.contentType && !defaultsAppliedRef.current) {
      applyDefaultBranding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, postData.contentType, isOpen, item?.id]);

  // Reset template to default configuration
  const handleResetTemplate = async () => {
    if (!selectedTemplate || !postData.contentType || isPublished || isResetting) {
      return;
    }

    setIsResetting(true);
    try {
      // Fetch template meta to get default parameters (pass postId to get post-specific defaults)
      const metaData = await fetchTemplateMeta(selectedTemplate, item?.id);

      if (!metaData?.defaultBranding) {
        addToast('No default configuration available for this template', 'warning');
        setIsResetting(false);
        return;
      }

      // Build updates object with default values for config fields
      const updates: any = {};

      // Apply default branding values
      const defaultBranding = metaData.defaultBranding;
      if (defaultBranding.color1 !== undefined) updates.color1 = defaultBranding.color1;
      if (defaultBranding.color2 !== undefined) updates.color2 = defaultBranding.color2;
      if (defaultBranding.color3 !== undefined) updates.color3 = defaultBranding.color3;
      if (defaultBranding.logo !== undefined) updates.logo = defaultBranding.logo;
      if (defaultBranding['font-for-text'] !== undefined) updates.fontForText = defaultBranding['font-for-text'];
      if (defaultBranding.image1 !== undefined) updates.image1 = defaultBranding.image1;
      if (defaultBranding.image2 !== undefined) updates.image2 = defaultBranding.image2;
      if (defaultBranding.photo !== undefined) updates.photo = defaultBranding.photo;
      if (defaultBranding.background !== undefined) updates.background = defaultBranding.background;

      // Reset other config fields to empty strings if not in defaultBranding
      // Only reset if they're not in the defaultBranding object
      if (defaultBranding.backgroundColor === undefined) updates.backgroundColor = '';
      if (defaultBranding.speakerPhoto === undefined) updates.speakerPhoto = '';
      updates.brandHandle = '';
      updates.brandName = '';
      updates.phone = '';
      updates.website = '';
      updates.speakerRole = '';

      // Clear display URL map for reset images to ensure fresh preview
      displayUrlMapRef.current.clear();

      // Apply the updates (preserves content fields: title, subtitle, mainText, cta, bullets, date, time, plannedDate, document)
      updatePostData(updates);

      addToast('Template configuration reset to defaults', 'success');
    } catch (err: unknown) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setIsResetting(false);
    }
  };

  // Template change handler with version loading
  const handleTemplateChange = async (newTemplate: string) => {
    setSelectedTemplate(newTemplate);

    if (newTemplate === previousTemplateRef.current || !item?.id || !isOpen) return;
    previousTemplateRef.current = newTemplate;

    // Clear preview first to ensure old preview doesn't show
    setImageUrl(null);
    setIsLoadingRender(true);

    // Fetch template metadata first to ensure shouldShowField works correctly
    await fetchTemplateMeta(newTemplate, item?.id);

    // Check if this template has a saved version
    try {
      const versionResponse = await api.get(`/mxm/posts/${item.id}/template-versions/${newTemplate}`);
      const savedVersion = versionResponse.data;

      if (savedVersion) {
        // Load the saved version's content (columns) and config (JSONB)
        const config = savedVersion.config || {};

        // Convert date from Date object or string to YYYY-MM-DD format
        let dateValue = '';
        if (config.date) {
          if (typeof config.date === 'string') {
            dateValue = config.date.split('T')[0];
          } else if (config.date instanceof Date) {
            dateValue = config.date.toISOString().split('T')[0];
          }
        }

        // Use company logo as fallback if version doesn't have one
        let logoToUse = config.logo;
        if (!logoToUse && item?.company?.logoUrl) {
          logoToUse = item.company.logoUrl;
        }

        // When switching to an existing template version, load ALL data from saved version
        // This ensures the form shows the correct template-specific content
        updatePostData({
          // Content fields (from columns) - load from saved version
          title: savedVersion.title || '',
          subtitle: savedVersion.subtitle || '',
          mainText: savedVersion.mainText || '',
          cta: savedVersion.cta || '',

          // Config fields (from JSONB) - load from saved version
          // Images (with company logo fallback)
          logo: logoToUse || '',
          photo: config.photo || '',
          background: config.background || '',
          image1: config.image1 || '',
          image2: config.image2 || '',
          speakerPhoto: config.speakerPhoto || '',

          // Colors & Branding
          color1: config.color1 || '',
          color2: config.color2 || '',
          color3: config.color3 || '',
          backgroundColor: config.backgroundColor || '',
          fontForText: config.fontForText || '',

          // Content extras
          imageGeneratedTopic: config.imageGeneratedTopic || '',
          bullets: config.bullets && Array.isArray(config.bullets) ? config.bullets : [],

          // Date & Time
          date: dateValue,
          time: config.time || '',

          // Speaker & Contact
          speakerRole: config.speakerRole || '',
          phone: config.phone || '',
          website: config.website || '',

          // Brand info
          brandHandle: config.brandHandle || '',
          brandName: config.brandName || '',

          templateName: newTemplate,
        });

        // Set this version as active
        try {
          await api.put(`/mxm/posts/${item.id}/active-template-version/${savedVersion.id}`);
        } catch (error: unknown) {
          console.error('Failed to set active version:', error);
          // Don't fail the whole operation if this fails
        }

        // Wait a bit for state to update
        await new Promise(r => setTimeout(r, 100));

        // Load saved render if available and valid
        if (savedVersion.render?.imageUrl) {
          // Force clear first, then set new image to ensure React updates
          setImageUrl(null);
          await new Promise(r => setTimeout(r, 50));
          // Set the preview image from saved render
          setImageUrl(savedVersion.render.imageUrl);
          setIsLoadingRender(false);
          addToast('Loaded saved template version', 'success');
          return;
        } else {
          // Version exists but no render - auto-render it
          try {
            // Fetch template meta to get defaults (in case some branding is missing)
            const metaData = await fetchTemplateMeta(newTemplate, item?.id);

            // Apply any missing defaults from template meta
            // Check both current form data AND saved version config
            const updates: any = {};
            if (metaData?.defaultBranding) {
              // Get current values (from form or saved version)
              const currentLogo = postData.logo || config.logo;
              const currentPhoto = postData.photo || config.photo;
              const currentBackground = postData.background || config.background;
              const currentColor1 = postData.color1 || config.color1;
              const currentColor2 = postData.color2 || config.color2;
              const currentColor3 = postData.color3 || config.color3;
              const currentFont = postData.fontForText || config.fontForText;
              const currentImage1 = postData.image1 || config.image1;
              const currentImage2 = postData.image2 || config.image2;

              // Only apply defaults if missing in both current form AND saved version
              if (metaData.defaultBranding.color1 && !currentColor1) updates.color1 = metaData.defaultBranding.color1;
              if (metaData.defaultBranding.color2 && !currentColor2) updates.color2 = metaData.defaultBranding.color2;
              if (metaData.defaultBranding.color3 && !currentColor3) updates.color3 = metaData.defaultBranding.color3;
              if (metaData.defaultBranding.logo && !currentLogo) updates.logo = metaData.defaultBranding.logo;
              if (metaData.defaultBranding['font-for-text'] && !currentFont) updates.fontForText = metaData.defaultBranding['font-for-text'];
              if (metaData.defaultBranding.image1 && !currentImage1) updates.image1 = metaData.defaultBranding.image1;
              if (metaData.defaultBranding.image2 && !currentImage2) updates.image2 = metaData.defaultBranding.image2;
              if (metaData.defaultBranding.photo && !currentPhoto) updates.photo = metaData.defaultBranding.photo;
              if (metaData.defaultBranding.background && !currentBackground) updates.background = metaData.defaultBranding.background;
            }

            // Update post data with any missing defaults
            if (Object.keys(updates).length > 0) {
              updatePostData(updates);
              await new Promise(r => setTimeout(r, 100));
            }

            // IMPORTANT: Use CURRENT postData (which has user's edits) for rendering
            // This ensures ALL user edits (images, colors, text, etc.) are used, not old saved version data
            const payload = buildRenderPayload({
              ...postData, // Current form data with ALL user's edits preserved
              ...updates,  // Any missing defaults from template meta
              templateName: newTemplate
            });
            await triggerRender(item.id.toString(), payload);
            await fetchUpdatedRender(item.id.toString(), 2);
            addToast('Template version rendered successfully', 'success');
          } catch (error: unknown) {
            console.error('Failed to render:', error);
            addToast('Template version loaded but render failed', 'warning');
          } finally {
            setIsLoadingRender(false);
          }
          return;
        }
      }
    } catch (error: unknown) {
      // No saved version found, continue with defaults
      setIsLoadingRender(false);
    }

    // No saved version - fetch template meta for params and defaults, then render
    const metaData = await fetchTemplateMeta(newTemplate, item?.id);

    const updates: any = {
      templateName: newTemplate,
    };

    // Apply template defaults from meta (keep existing content fields)
    if (metaData?.defaultBranding) {
      if (metaData.defaultBranding.color1) updates.color1 = metaData.defaultBranding.color1;
      if (metaData.defaultBranding.color2) updates.color2 = metaData.defaultBranding.color2;
      if (metaData.defaultBranding.color3) updates.color3 = metaData.defaultBranding.color3;
      if (metaData.defaultBranding.logo) updates.logo = metaData.defaultBranding.logo;
      if (metaData.defaultBranding['font-for-text']) updates.fontForText = metaData.defaultBranding['font-for-text'];
      if (metaData.defaultBranding.image1) updates.image1 = metaData.defaultBranding.image1;
      if (metaData.defaultBranding.image2) updates.image2 = metaData.defaultBranding.image2;
    }

    updatePostData(updates);
    await new Promise(r => setTimeout(r, 100));

    // Auto-render new template - backend renderer will automatically create and save template version
    setIsLoadingRender(true);
    try {
      const payload: RenderPayload = buildRenderPayload({
        ...postData,
        ...updates,
        templateName: newTemplate
      });
      // Trigger render - renderer service will automatically create template version after rendering
      await triggerRender(item.id.toString(), payload);
      await fetchUpdatedRender(item.id.toString(), 2);
      addToast('Template rendered and version saved successfully', 'success');
    } catch (error: unknown) {
      addToast(getErrorMessage(error), 'error');
    } finally {
      setIsLoadingRender(false);
    }
  };

  // Save handler - Save as template version
  const handleSave = async () => {
    if (!item || !onSave || !item?.id) return;

    // Cancel any pending auto-save to prevent race conditions
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    isAutoSavingRef.current = false;

    setIsSaving(true);
    try {
      const isModified =
        !datesAreEqual(postData.plannedDate, originalPlannedDateRef.current ? new Date(originalPlannedDateRef.current) : null) ||
        selectedTemplate !== savedTemplateNameRef.current;
      // Save as template version (use helper function)
      const versionPayload = buildVersionPayload();
      await api.post(`/mxm/posts/${item.id}/template-versions`, versionPayload);

      // Also update the main post record for calendar changes
      await onSave(item, buildSavePayload({ ...postData, templateName: selectedTemplate }));

      if (isModified && onRefetchCalendar) {
        try {
          await onRefetchCalendar();
        } catch (calendarError: unknown) {
          console.error('Failed to refetch calendar:', calendarError);
        }
      }

      try {
        const renderPayload: RenderPayload = buildRenderPayload({ ...postData, templateName: selectedTemplate });
        await triggerRender(item.id.toString(), renderPayload);
        addToast('Template version saved and rendered successfully', 'success');
      } catch (renderError: unknown) {
        console.error('Failed to trigger render after save:', renderError);
        // Don't show non-critical error
      }

      onClose();
    } catch (error: unknown) {
      console.error('Failed to save:', error);
      addToast(getErrorMessage(error), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Refresh preview handler
  const handleRefresh = async () => {
    if (!item || !onSave || !item?.id) return;

    // Cancel any pending auto-save to prevent race conditions
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    isAutoSavingRef.current = false;

    setIsLoadingRender(true);
    try {
      const dateMoved = !datesAreEqual(
        postData.plannedDate,
        originalPlannedDateRef.current ? new Date(originalPlannedDateRef.current) : null
      );

      // Save to main post table
      await onSave(item, buildSavePayload({ ...postData, templateName: selectedTemplate }));

      // CRITICAL: Save template version FIRST with latest config/images before rendering
      // This ensures the renderer uses the updated config, not old template version data
      let savedVersionId: number | undefined;
      try {
        const versionPayload = buildVersionPayload();
        const versionResponse = await api.post(`/mxm/posts/${item.id}/template-versions`, versionPayload);
        savedVersionId = versionResponse.data?.id; // Get the version ID from the response
      } catch (versionError: unknown) {
        console.error('Failed to save template version:', versionError);
        // Continue with render even if version save fails (renderer will create/update version)
      }

      if (dateMoved && onRefetchCalendar) {
        try {
          await onRefetchCalendar();
        } catch (calendarError: unknown) {
          console.error('Failed to refetch calendar:', calendarError);
        }
      }

      await new Promise((r) => setTimeout(r, 300));

      try {
        // Pass versionId if available to ensure renderer uses the exact version we just saved
        const renderPayload: RenderPayload = buildRenderPayload({
          ...postData,
          templateName: selectedTemplate,
          versionId: savedVersionId
        });

        await triggerRender(item.id.toString(), renderPayload);

        const success = await fetchUpdatedRender(item.id.toString(), 3);
        if (success) {
          addToast('Data updated and preview refreshed successfully', 'success');
        } else {
          setImageUrl(null);
          addToast('Data updated, but preview image not available yet', 'warning');
        }
      } catch (renderError: unknown) {
        console.error('Failed to trigger render:', renderError);
        const errorMsg = getErrorMessage(renderError);
        if (errorMsg.includes('logo') || errorMsg.includes('Logo')) {
          addToast('Data updated, but render failed: Logo is required for rendering', 'warning');
        } else {
          addToast('Data updated successfully, but render failed', 'warning');
        }
      }
    } catch (error: unknown) {
      console.error('Failed to save:', error);
      addToast(getErrorMessage(error), 'error');
    } finally {
      setIsLoadingRender(false);
    }
  };

  // Publish handlers
  const handlePublishNowClick = () => {
    if (!item || !item?.id) return;

    if (!postData.title || postData.title.trim() === '') {
      addToast('Title is required to publish. Please add a title before publishing.', 'error');
      return;
    }

    setShowPublishConfirm(true);
  };

  const handlePublishConfirm = async () => {
    setShowPublishConfirm(false);
    await performPublish();
  };

  const handlePublishCancel = () => {
    setShowPublishConfirm(false);
  };

  const performPublish = async () => {
    if (!item || !item?.id) return;

    if (!imageUrl) {
      const confirmPublish = window.confirm(
        'No preview image found. The post may not render correctly on LinkedIn. Do you want to continue?'
      );
      if (!confirmPublish) return;
    }

    setIsPublishing(true);
    try {
      if (onSave) {
        try {
          await onSave(item, buildSavePayload({ ...postData, templateName: selectedTemplate }));
        } catch (saveError: unknown) {
          console.error('Failed to save before publishing:', saveError);
        }
      }

      const postText = buildPostText({
        title: postData.title,
        subtitle: postData.subtitle,
        mainText: postData.mainText,
        cta: postData.cta,
      });

      const publishPayload: PublishPayload = {
        text: postText,
        postId: item.id,
        title: postData.title,
        imageUrl: imageUrl || undefined,
      };

      const response = await api.post('/mxm/posts/publish/linkedin', publishPayload);

      if (response.data?.url) {
        updatePostData({ postStatus: 'published' });
        addToast(`Post published successfully! View it on LinkedIn: ${response.data.url}`, 'success');

        if (onRefetchCalendar) {
          try {
            await onRefetchCalendar();
          } catch (calendarError: unknown) {
            console.error('Failed to refetch calendar after publishing:', calendarError);
          }
        }
      } else {
        addToast('Post published, but no LinkedIn URL was returned', 'warning');
      }
    } catch (error: unknown) {
      console.error('Failed to publish post:', error);
      const errorMessage = getErrorMessage(error);

      if (errorMessage.includes('LinkedIn access token') || errorMessage.includes('token')) {
        addToast('LinkedIn connection required. Please connect your LinkedIn account first.', 'error');
      } else if (errorMessage.includes('permission') || errorMessage.includes('access')) {
        addToast('LinkedIn posting permission required. Please check your LinkedIn connection.', 'error');
      } else {
        addToast(errorMessage, 'error');
      }
    } finally {
      setIsPublishing(false);
    }
  };

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Content" size="xl" showCloseButton={true}>
      {/* Published Badge */}
      {isPublished && (
        <div className="mb-4 flex justify-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
            Published
          </span>
        </div>
      )}

      <div className="flex gap-6 min-h-[400px]">
        {/* Left Side - Preview Image */}
        <div className="shrink-0 w-[480px] flex flex-col gap-4">
          <div className="h-[480px] editContentModal relative">
            <div className="h-full overflow-hidden border border-gray-200 transition-colors duration-300 relative">
              {isLoadingRender ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    <div className="text-gray-500 text-sm">Loading preview...</div>
                  </div>
                </div>
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Content preview"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const imgEl = e.target as HTMLImageElement;
                    imgEl.src = '/No-Template_Found.png';
                    imgEl.className = 'w-full h-full object-cover opacity-90';
                  }}
                />
              ) : (
                <img src="/No-Template_Found.png" alt="Content preview" className="w-full h-full object-cover opacity-90" />
              )}
            </div>
          </div>

          {/* Document - Below Preview */}
          {documentInfo && (
            <div className="border border-gray-200 rounded overflow-hidden">
              <button
                onClick={async () => {
                  if (!documentInfo) return;
                  setLoadingDocumentUrl(true);
                  try {
                    const response = await api.get(`/upload/${documentInfo.mxmUploadId}/url`);
                    if (response.data?.url) {
                      const link = document.createElement('a');
                      link.href = response.data.url;
                      link.download = documentInfo.filename;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      addToast('Document download started', 'success');
                    } else {
                      addToast('Failed to get document URL', 'error');
                    }
                  } catch (error: unknown) {
                    console.error('Failed to fetch document URL:', error);
                    addToast(getErrorMessage(error), 'error');
                  } finally {
                    setLoadingDocumentUrl(false);
                  }
                }}
                disabled={loadingDocumentUrl}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                title={documentInfo.filename}
              >
                {loadingDocumentUrl ? (
                  <>
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin shrink-0" />
                    <span className="text-sm text-gray-600">Preparing download...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                    <span className="text-sm text-gray-700 truncate flex-1 text-left">{documentInfo.filename}</span>
                    <Download className="w-4 h-4 text-gray-400 shrink-0" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Right Side - Form Fields */}
        <div className="flex-1 flex flex-col gap-4 max-h-[600px] overflow-y-auto pr-2">
          <div className="space-y-4">
            {/* Template Selector */}
            {postData.contentType && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Select
                    label="Template"
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    options={
                      loadingTemplates
                        ? [{ value: '', label: 'Loading...' }]
                        : templates.length > 0
                          ? templates.map((template) => ({ value: template, label: formatTemplateName(template) }))
                          : [{ value: '', label: 'No templates' }]
                    }
                    disabled={loadingTemplates || templates.length === 0 || isPublished}
                    className="w-full"
                  />
                </div>
                <Button
                  onClick={handleResetTemplate}
                  variant="secondary"
                  size="md"
                  className="bg-transparent border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isPublished || !selectedTemplate || loadingTemplates || isResetting}
                  title="Reset template configuration to defaults"
                >
                  {isResetting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Basic Info Section */}
            <BasicInfoSection
              title={postData.title}
              subtitle={postData.subtitle}
              mainText={postData.mainText}
              onTitleChange={(value) => updatePostData({ title: value })}
              onSubtitleChange={(value) => updatePostData({ subtitle: value })}
              onMainTextChange={(value) => updatePostData({ mainText: value })}
              disabled={isPublished}
            />

            {/* Additional Fields Section */}
            <AdditionalFieldsSection
              cta={postData.cta}
              bullets={postData.bullets}
              date={postData.date}
              time={postData.time}
              speakerRole={postData.speakerRole}
              phone={postData.phone}
              website={postData.website}
              onCtaChange={(value) => updatePostData({ cta: value })}
              onBulletsChange={(bullets) => updatePostData({ bullets })}
              onDateChange={(value) => updatePostData({ date: value })}
              onTimeChange={(value) => updatePostData({ time: value })}
              onSpeakerRoleChange={(value) => updatePostData({ speakerRole: value })}
              onPhoneChange={(value) => updatePostData({ phone: value })}
              onWebsiteChange={(value) => updatePostData({ website: value })}
              shouldShowBullets={shouldShowField('bullets')}
              shouldShowDate={shouldShowField('date')}
              shouldShowTime={shouldShowField('time')}
              shouldShowSpeakerRole={shouldShowField('speaker_role')}
              shouldShowPhone={shouldShowField('phone')}
              shouldShowWebsite={shouldShowField('website')}
              disabled={isPublished}
            />

            {/* Media Section */}
            <MediaSection
              logo={postData.logo}
              photo={postData.photo}
              background={postData.background}
              image1={postData.image1}
              image2={postData.image2}
              speakerPhoto={postData.speakerPhoto}
              getImageUrl={getImageUrl}
              onLogoChange={(value) => {
                updatePostData({ logo: value });
                // Images are only saved when user clicks Save or Refresh
              }}
              onPhotoChange={(value) => {
                updatePostData({ photo: value });
                // Images are only saved when user clicks Save or Refresh
              }}
              onBackgroundChange={(value) => {
                updatePostData({ background: value });
                // Images are only saved when user clicks Save or Refresh
              }}
              onImage1Change={(value) => {
                updatePostData({ image1: value });
                // Images are only saved when user clicks Save or Refresh
              }}
              onImage2Change={(value) => {
                updatePostData({ image2: value });
                // Images are only saved when user clicks Save or Refresh
              }}
              onSpeakerPhotoChange={(value) => {
                updatePostData({ speakerPhoto: value });
                // Images are only saved when user clicks Save or Refresh
              }}
              onLogoUpload={handleLogoUpload}
              onPhotoUpload={handlePhotoUpload}
              onBackgroundUpload={handleBackgroundUpload}
              onImage1Upload={handleImage1Upload}
              onImage2Upload={handleImage2Upload}
              onSpeakerPhotoUpload={handleSpeakerPhotoUpload}
              uploadingLogo={uploadingLogo}
              uploadingPhoto={uploadingPhoto}
              uploadingBackground={uploadingBackground}
              uploadingImage1={uploadingImage1}
              uploadingImage2={uploadingImage2}
              uploadingSpeakerPhoto={uploadingSpeakerPhoto}
              shouldShowLogo={shouldShowField('logo')}
              shouldShowPhoto={shouldShowField('photo')}
              shouldShowBackground={shouldShowField('background')}
              shouldShowImage1={shouldShowField('image1')}
              shouldShowImage2={shouldShowField('image2')}
              shouldShowSpeakerPhoto={shouldShowField('speaker_photo')}
              disabled={isPublished}
            />

            {/* Branding Section */}
            <BrandingSection
              color1={postData.color1}
              color2={postData.color2}
              color3={postData.color3}
              backgroundColor={postData.backgroundColor}
              fontForText={postData.fontForText}
              brandHandle={postData.brandHandle}
              brandName={postData.brandName}
              onColor1Change={(value) => {
                updatePostData({ color1: value });
                autoSaveTemplateVersion();
              }}
              onColor2Change={(value) => {
                updatePostData({ color2: value });
                autoSaveTemplateVersion();
              }}
              onColor3Change={(value) => {
                updatePostData({ color3: value });
                autoSaveTemplateVersion();
              }}
              onBackgroundColorChange={(value) => {
                updatePostData({ backgroundColor: value });
                autoSaveTemplateVersion();
              }}
              onFontChange={(value) => {
                updatePostData({ fontForText: value });
                autoSaveTemplateVersion();
              }}
              onBrandHandleChange={(value) => {
                updatePostData({ brandHandle: value });
                autoSaveTemplateVersion();
              }}
              onBrandNameChange={(value) => {
                updatePostData({ brandName: value });
                autoSaveTemplateVersion();
              }}
              shouldShowBackgroundColor={shouldShowField('background_color')}
              shouldShowBrandHandle={shouldShowField('brand_handle')}
              shouldShowBrandName={shouldShowField('brand_name')}
              shouldShowColor3={shouldShowField('color3')}
              disabled={isPublished}
            />

            {/* Publishing Date */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Publishing Date</label>
              <DatePicker
                value={postData.plannedDate || undefined}
                onChange={(date) => updatePostData({ plannedDate: date })}
                showTime={false}
                placeholder="Select planned date"
                min={new Date()}
                className="w-full border-slate-300 focus:ring-slate-900/10 focus:border-slate-400"
                disabled={isPublished}
              />
            </div>
          </div>

          {/* Preview Div */}
          <div className="flex-1 rounded-lg border border-gray-200 overflow-hidden mt-4">
            <div className="w-full h-full min-h-[200px] p-6 transition-colors duration-300" style={{ backgroundColor: postData.color1 }}>
              <div className="text-white">
                {postData.logo && (
                  <div className="mb-4">
                    <img
                      src={getImageUrl(postData.logo)}
                      alt="Logo"
                      className="h-12 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {postData.title && <h3 className="text-xl font-semibold mb-2">{postData.title}</h3>}
                {postData.subtitle && <p className="text-lg opacity-90 mb-2">{postData.subtitle}</p>}
                <p className="text-sm opacity-80">{item.preview}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Hidden when published */}
      {!isPublished && (
        <ModalFooter align="between">
          <div className="flex gap-3 justify-end w-full">
            <Button
              onClick={handleRefresh}
              variant="primary"
              size="md"
              className="bg-slate-900 text-white hover:bg-slate-800 border-0"
              disabled={isPublishing || isSaving}
            >
              Refresh
            </Button>
            {hasLinkedInToken && (
              <Button
                onClick={handlePublishNowClick}
                variant="secondary"
                size="md"
                disabled={isPublishing || isSaving}
                className="bg-transparent border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white hover:border-slate-900 text-dark transition-colors"
              >
                {isPublishing ? 'Publishing...' : 'Publish Now'}
              </Button>
            )}
            <Button
              onClick={handleSave}
              variant="primary"
              size="md"
              disabled={isSaving || isPublishing}
              className="bg-teal-600 hover:bg-teal-700 text-white border-0"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </ModalFooter>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPublishConfirm}
        title="Publish Post to LinkedIn?"
        message="Are you sure you want to publish this post to LinkedIn? This action cannot be undone."
        confirmText="Publish"
        cancelText="Cancel"
        variant="warning"
        onConfirm={handlePublishConfirm}
        onCancel={handlePublishCancel}
        isLoading={isPublishing}
      />
    </Modal>
  );
};

